import { useContext, type ReactElement, type ReactNode } from 'react';
import moment from 'jalali-moment';
import { Link } from 'react-router-dom';
import { Dropdown, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Doughnut } from 'react-chartjs-2';
import type { ChartData, ChartOptions } from 'chart.js';
import type {
  ColDef,
  ColumnMenuTab,
  ICellRendererParams,
  ValueFormatterParams
} from 'ag-grid-community';
import URLRenderer from '@/app/common/components/CellRenderers/URLRenderer';
import SubtleBadge from '@/components/common/SubtleBadge';
import {
  decodeGetter,
  valueFormatter
} from '@/app/common/components/ag-grid/configs';
import AiContentHistoryStatusRenderer from '@/app/ContentTools/components/AiContentHistoryStatusRenderer';
import { getColor } from '@/helpers/utils';
import { aiAnalysisDetailsPath } from '@/app/ContentTools/utils/aiAnalysisPaths';
import type { AiAnalysisDetailsTab } from '@/app/ContentTools/utils/aiAnalysisPaths';
import { RemoveAiContentContext } from '@/context/Context';
import { warningMessage } from '@/utils/toast';
import type {
  AiContentHistoryRow,
  ContentAnalysisType
} from '@/app/ContentTools/types/aiContentAnalysisApiTypes';

const menuTabs: ColumnMenuTab[] = [
  'columnsMenuTab',
  'filterMenuTab',
  'generalMenuTab'
];

type AiContentColumn = ColDef<AiContentHistoryRow> & {
  searchable?: boolean;
  title?: string;
  type?: string;
  noFilter?: boolean;
};

type ReRunHandler = (row: AiContentHistoryRow) => void;

type AiContentHistoryColumnsOptions = {
  onReRun?: ReRunHandler;
};

type RemoveAiContentContextValue = {
  setId: (id: string) => void;
  setKeyword: (keyword: string) => void;
  setShow: (show: boolean) => void;
};

type PersianDigitString = string & {
  toPersianDigit: () => string;
};

const toPersianDigit = (value: string) =>
  (value as PersianDigitString).toPersianDigit();

const SimpleURLRenderer = URLRenderer as (props: {
  url?: string | null;
}) => ReactElement;

const scoreChartOptions = {
  rotation: -90,
  animation: false,
  circumference: 180,
  cutout: '60%',
  hover: { mode: null },
  plugins: {
    legend: { display: false },
    tooltip: { enabled: false }
  }
} as unknown as ChartOptions<'doughnut'>;

const HUMAN_LIKENESS_COLOR = '#10b981';
const AI_LIKENESS_COLOR = '#ef4444';

const ScoreDoughnut = ({
  data,
  tooltip
}: {
  data: ChartData<'doughnut'>;
  tooltip: ReactNode;
}) => (
  <OverlayTrigger
    placement="top"
    overlay={<Tooltip>{tooltip}</Tooltip>}
  >
    <div style={{ width: '46px', height: '40px' }}>
      <Doughnut data={data} options={scoreChartOptions} />
    </div>
  </OverlayTrigger>
);

const getScoreColor = (score: number) => {
  if (Number.isNaN(score)) return getColor('gray-300');
  // ۰–۲: تقریباً ناممکن (قرمز تیره)
  if (score < 2) return '#b50000';
  // ۲–۴: بسیار ضعیف (قرمز)
  if (score < 4) return '#ef4444';
  // ۴–۶: متوسط رو به پایین (نارنجی)
  if (score < 6) return '#f97316';
  // ۶–۸: متوسط رو به بالا (زرد)
  if (score < 8) return '#dfba03';
  // ۸–۱۰: عالی (سبز)
  return '#10b981';
};

type ActionsCellRendererProps = ICellRendererParams<AiContentHistoryRow> & {
  domainSlug?: string;
  domain?: string;
  onReRun?: ReRunHandler;
};

const ANALYSIS_TYPE_BADGE: Record<
  ContentAnalysisType,
  { label: string; tab: AiAnalysisDetailsTab; bg: string }
> = {
  seo_analysis: { label: 'تحلیل سئو', tab: 'seo', bg: 'primary' },
  ai_detection: { label: 'تشخیص AI', tab: 'detector', bg: 'warning' },
  eeat_analysis: { label: 'تحلیل EEAT', tab: 'eeat', bg: 'info' },
  ai_search: { label: 'تحلیل GEO', tab: 'geo', bg: 'success' }
};

const ActionsCellRenderer = ({
  data,
  domainSlug,
  domain,
  onReRun
}: ActionsCellRendererProps) => {
  const { setId: setRemId, setKeyword: setRemKeyword, setShow } = useContext(
    RemoveAiContentContext
  ) as RemoveAiContentContextValue;

  const overviewPath =
    data?.id && domainSlug && domain
      ? aiAnalysisDetailsPath('overview', domainSlug, domain, data.id)
      : null;

  const handleRerun = () => {
    if (!onReRun || !data) return;
    onReRun(data);
  };

  const handleDelete = () => {
    if (!data?.id) {
      warningMessage('شناسه معتبر برای حذف پیدا نشد');
      return;
    }
    setRemKeyword(data.keyword || '');
    setRemId(String(data.id));
    setShow(true);
  };

  return (
    <span className="allaho_akbar_zIndex">
      <Dropdown
        onClick={e => e.stopPropagation()}
        align="end"
        className="allaho_akbar_zIndex overflow-visible btn-reveal-trigger d-inline-block me-0"
      >
        <Dropdown.Toggle
          className="text-600 btn-reveal"
          split
          variant="reveal"
          size="sm"
        >
          <FontAwesomeIcon icon="ellipsis-h" className="fs--2" />
        </Dropdown.Toggle>
        <Dropdown.Menu className="border py-0">
          {overviewPath && (
            <Dropdown.Item
              as={Link}
              to={overviewPath}
              className="link-600 fs--1 d-flex align-items-center gap-1 p-0 px-1"
            >
              <FontAwesomeIcon icon="eye" className="me-1 ms-2" />
              مشاهده
            </Dropdown.Item>
          )}
          <Dropdown.Item
            onClick={handleRerun}
            className="link-600 fs--1 d-flex align-items-center gap-1 p-0 px-1"
          >
            <FontAwesomeIcon icon="undo-alt" className="me-1 ms-2" />
            جستجوی مجدد
          </Dropdown.Item>
          <Dropdown.Divider className="my-0" />
          <Dropdown.Item
            onClick={handleDelete}
            className="link-600 text-danger fs--1 d-flex align-items-center gap-1 p-0 px-1"
          >
            <FontAwesomeIcon icon="trash-alt" className="me-1 fs-0 ms-2" />
            حذف
          </Dropdown.Item>
        </Dropdown.Menu>
      </Dropdown>
    </span>
  );
};

export const AiContentHistoryColumns = (
  domain_slug?: string,
  domain?: string,
  { onReRun }: AiContentHistoryColumnsOptions = {}
): AiContentColumn[] => [
  {
    searchable: true,
    menuTabs,
    filter: 'agTextColumnFilter',
    floatingFilter: true,
    filterValueGetter: decodeGetter,
    field: 'keyword',
    type: 'string',
    title: 'کلمه کلیدی',
    cellClass: 'over-flow',
    cellRenderer: (
      params: ICellRendererParams<AiContentHistoryRow, string>
    ) => {
      const keywordLabel = valueFormatter(params) || '—';
      const analysisId = params.data?.id;

      if (!analysisId || !domain_slug || !domain) {
        return <span className="fw-bolder">{keywordLabel}</span>;
      }

      return (
        <Link
          to={aiAnalysisDetailsPath(
            'overview',
            domain_slug,
            domain,
            analysisId
          )}
          style={{
            color: getColor('navbar-vertical-default-link-active-color')
          }}
          className="fw-bolder btn-link"
        >
          {keywordLabel}
        </Link>
      );
    },
    flex: 1,
    minWidth: 180
  },
  {
    searchable: false,
    menuTabs,
    filter: 'agTextColumnFilter',
    floatingFilter: true,
    field: 'analysis_types',
    type: 'string',
    title: 'نوع تحلیل',
    width: 330,
    minWidth: 330,
    cellClass: 'over-flow',
    valueGetter: (params: { data?: AiContentHistoryRow }) =>
      (params.data?.analysis_types || [])
        .map(type => ANALYSIS_TYPE_BADGE[type as ContentAnalysisType]?.label)
        .filter(Boolean)
        .join('، '),
    cellRenderer: (params: ICellRendererParams<AiContentHistoryRow>) => {
      const canNavigate =
        Boolean(params.data?.id) && params.data?.error_code === null;
      const analysisTypes = (params.data?.analysis_types || []).filter(
        (type): type is ContentAnalysisType =>
          type === 'seo_analysis' ||
          type === 'ai_detection' ||
          type === 'eeat_analysis' ||
          type === 'ai_search'
      );

      if (analysisTypes.length === 0) return '';

      return (
        <div className="d-flex gap-1 align-items-center flex-nowrap">
          {analysisTypes.map(type => {
            const badge = ANALYSIS_TYPE_BADGE[type];
            const badgeEl = (
              <SubtleBadge bg={badge.bg} pill className="fs--2">
                {badge.label}
              </SubtleBadge>
            );

            if (!canNavigate || !domain_slug || !domain) {
              return <span key={type}>{badgeEl}</span>;
            }

            return (
              <Link
                key={type}
                to={aiAnalysisDetailsPath(
                  badge.tab,
                  domain_slug,
                  domain,
                  params.data?.id || 'last'
                )}
                onClick={event => event.stopPropagation()}
                className="text-decoration-none"
              >
                {badgeEl}
              </Link>
            );
          })}
        </div>
      );
    }
  },
  {
    searchable: true,
    menuTabs,
    filter: 'agTextColumnFilter',
    floatingFilter: true,
    filterValueGetter: decodeGetter,
    field: 'url',
    type: 'string',
    title: 'آدرس صفحه',
    flex: 1,
    minWidth: 240,
    cellRenderer: (params: ICellRendererParams<AiContentHistoryRow, string>) => (
      <SimpleURLRenderer url={params.value} />
    )
  },
  {
    searchable: false,
    menuTabs,
    filter: 'agTextColumnFilter',
    floatingFilter: true,
    field: 'error_code',
    type: 'number',
    title: 'وضعیت',
    width: 125,
    minWidth: 115,
    cellRenderer: (
      params: ICellRendererParams<
        AiContentHistoryRow,
        AiContentHistoryRow['error_code']
      >
    ) => (
      <AiContentHistoryStatusRenderer
        errorCode={params.value}
        errorMessage={params.data?.error_message}
        errorDetails={params.data?.error_details}
        errorNet={params.data?.error_net}
      />
    )
  },
  {
    searchable: false,
    menuTabs,
    filter: 'agTextColumnFilter',
    floatingFilter: true,
    field: 'rank',
    type: 'number',
    title: 'امتیازها',
    width: 220,
    cellStyle: { textAlign: 'start' },
    cellRenderer: (
      params: ICellRendererParams<AiContentHistoryRow, string | number>
    ) => {
      const rank = params.data?.rank;
      const seoScoreValue =
        rank === null || rank === undefined || rank === ''
          ? NaN
          : Number(rank);
      const seoScore =
        Number.isFinite(seoScoreValue)
          ? Math.max(0, Math.min(10, seoScoreValue > 10 ? seoScoreValue / 10 : seoScoreValue))
          : null;
      const seoChart =
        seoScore !== null
          ? {
              labels: ['Score', 'Remaining'],
              datasets: [
                {
                  data: [seoScore, 10 - seoScore],
                  backgroundColor: [getScoreColor(seoScore), getColor('gray-300')],
                  borderColor: [getScoreColor(seoScore), getColor('gray-300')]
                }
              ]
            }
          : null;

      const humanLikeness = params.data?.human_likeness;
      const aiLikeness = params.data?.ai_likeness;
      const hasLikeness =
        typeof humanLikeness === 'number' && typeof aiLikeness === 'number';
      const remainingLikeness = hasLikeness
        ? Math.max(0, 100 - humanLikeness - aiLikeness)
        : 0;
      const likenessChart = hasLikeness
        ? {
            labels: ['Human', 'AI', 'Remaining'],
            datasets: [
              {
                data: [humanLikeness, aiLikeness, remainingLikeness],
                backgroundColor: [
                  HUMAN_LIKENESS_COLOR,
                  AI_LIKENESS_COLOR,
                  getColor('gray-300')
                ],
                borderColor: [
                  HUMAN_LIKENESS_COLOR,
                  AI_LIKENESS_COLOR,
                  getColor('gray-300')
                ]
              }
            ]
          }
        : null;

      const eeatScoreRaw = params.data?.eeat_score;
      const eeatScoreValue =
        eeatScoreRaw === null || eeatScoreRaw === undefined
          ? NaN
          : Number(eeatScoreRaw);
      const eeatScore = Number.isFinite(eeatScoreValue)
        ? Math.max(0, Math.min(100, eeatScoreValue))
        : null;
      const eeatChart =
        eeatScore !== null
          ? {
              labels: ['EEAT', 'Remaining'],
              datasets: [
                {
                  data: [eeatScore, 100 - eeatScore],
                  backgroundColor: [
                    getScoreColor(eeatScore / 10),
                    getColor('gray-300')
                  ],
                  borderColor: [
                    getScoreColor(eeatScore / 10),
                    getColor('gray-300')
                  ]
                }
              ]
            }
          : null;

      const geoScoreRaw = params.data?.geo_score;
      const geoScoreValue =
        geoScoreRaw === null || geoScoreRaw === undefined
          ? NaN
          : Number(geoScoreRaw);
      const geoScore = Number.isFinite(geoScoreValue)
        ? Math.max(0, Math.min(100, geoScoreValue))
        : null;
      const geoChart =
        geoScore !== null
          ? {
              labels: ['GEO', 'Remaining'],
              datasets: [
                {
                  data: [geoScore, 100 - geoScore],
                  backgroundColor: [
                    getScoreColor(geoScore / 10),
                    getColor('gray-300')
                  ],
                  borderColor: [
                    getScoreColor(geoScore / 10),
                    getColor('gray-300')
                  ]
                }
              ]
            }
          : null;

      if (!seoChart && !likenessChart && !eeatChart && !geoChart) return '';

      return (
        <div className="d-flex align-items-center gap-2">
          {seoChart ? (
            <ScoreDoughnut
              data={seoChart}
              tooltip={
                <>
                  امتیاز سئو: {toPersianDigit(seoScore!.toFixed(1))} از{' '}
                  {toPersianDigit('10')}
                </>
              }
            />
          ) : null}
          {likenessChart ? (
            <ScoreDoughnut
              data={likenessChart}
              tooltip={
                <div className="text-start">
                  <div>
                    انسان: ٪{toPersianDigit(`${Math.round(humanLikeness!)}`)}
                  </div>
                  <div>
                    هوش مصنوعی: ٪{toPersianDigit(`${Math.round(aiLikeness!)}`)}
                  </div>
                </div>
              }
            />
          ) : null}
          {eeatChart ? (
            <ScoreDoughnut
              data={eeatChart}
              tooltip={
                <div>
                  <div>امتیاز EEAT</div>
                  <div>
                    {toPersianDigit(`${Math.round(eeatScore!)}`)} از{' '}
                    {toPersianDigit('100')}
                  </div>
                </div>
              }
            />
          ) : null}
          {geoChart ? (
            <ScoreDoughnut
              data={geoChart}
              tooltip={
                <div>
                  <div>امتیاز GEO</div>
                  <div>
                    {toPersianDigit(`${Math.round(geoScore!)}`)} از{' '}
                    {toPersianDigit('100')}
                  </div>
                </div>
              }
            />
          ) : null}
        </div>
      );
    }
  },
  {
    searchable: false,
    menuTabs,
    filter: 'agTextColumnFilter',
    floatingFilter: true,
    field: 'created_at',
    type: 'string',
    title: 'تاریخ',
    width: 140,
    valueFormatter: (
      params: ValueFormatterParams<AiContentHistoryRow, string>
    ) => {
      if (!params.value) return '';
      try {
        const m = moment(params.value);
        return toPersianDigit(m.locale('fa').format('YYYY/MM/DD HH:mm'));
      } catch {
        return params.value;
      }
    }
  },
  {
    field: 'actions',
    type: 'array',
    title: ' ',
    searchable: false,
    cellStyle: { textAlign: 'center', margin: '0px' },
    cellClass: 'over-flow',
    width: 70,
    pinned: 'left',
    noFilter: true,
    cellRenderer: (params: ICellRendererParams<AiContentHistoryRow>) => (
      <ActionsCellRenderer
        {...params}
        domainSlug={domain_slug}
        domain={domain}
        onReRun={onReRun}
      />
    )
  }
];
